import { useDisclosure } from '@mantine/hooks'
import type { Attachment } from '@medplum/fhirtypes'
import { ExtendedCoverage } from '@seen/core/models/coverage'
import { DocumentCategory } from '@seen/core/models/document-references/constants'
import { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import { Button } from '@seen/ui'
import { groupBy, sortBy } from 'lodash-es'
import { type JSX, useCallback, useMemo, useState } from 'react'
import { CoverageCard } from '#src/components/coverage/CoverageCard.tsx'
import { CoverageDialog } from '#src/components/coverage/CoverageDialog.tsx'
import { LinkDocumentsModal } from '#src/components/coverage/LinkDocumentsModal.tsx'
import { useCurrentParticipant } from '#src/contexts/current-participant/useCurrentParticipant.ts'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useSuspenseSearch } from '#src/hooks/useSuspenseSearch.ts'

export function PatientCoveragePage(): JSX.Element {
  const [showCreateModal, { open: openCreateModal, close: closeCreateModal }] =
    useDisclosure(false)
  const [
    showLinkDocumentsModal,
    { open: openLinkDocumentsModal, close: closeLinkDocumentsModal },
  ] = useDisclosure(false)
  const [showEditModal, { open: openEditModal, close: closeEditModal }] =
    useDisclosure(false)
  const [selectedCoverage, setSelectedCoverage] =
    useState<ExtendedCoverage | null>(null)

  const participant = useCurrentParticipant()

  const { data: coverages } = useSuspenseSearch(
    ExtendedCoverage,
    ExtendedCoverage.byParticipant({ participant: participant.reference })
  )

  const { data: documentReferences, refetch: refetchDocumentReferences } =
    useSuspenseSearch(
      ExtendedDocumentReference,
      ExtendedDocumentReference.byCoverage({
        participant: participant.reference,
      })
    )

  const documentReferencesByCoverage = useMemo(
    () => groupBy(documentReferences, (doc) => doc.coverage?.reference),
    [documentReferences]
  )

  const { mutate: createDocument } =
    useCreateMutation<ExtendedDocumentReference>()
  const patchDocumentMutation = usePatchMutation<ExtendedDocumentReference>()
  const practitioner = useCurrentPractitioner()
  const handleUpload = useCallback(
    (coverage: ExtendedCoverage, newAttachments: Attachment[]): void => {
      const newDocRef = new ExtendedDocumentReference({
        subject: participant.reference,
        author: practitioner.reference,
        attachments: newAttachments,
        categories: [DocumentCategory.ENROLLMENT],
      })
      newDocRef.coverage = coverage.reference
      newDocRef.typeCode = coverage.documentType
      createDocument(newDocRef, {
        onSuccess: () => {
          void refetchDocumentReferences()
        },
      })
    },
    [
      createDocument,
      participant.reference,
      practitioner.reference,
      refetchDocumentReferences,
    ]
  )

  return (
    <>
      <div className="flex max-w-full flex-col gap-4">
        <div className="flex justify-end">
          <Button variant="outline" onClick={openCreateModal}>
            Add Coverage
          </Button>
        </div>

        <div className="flex flex-col gap-6">
          {sortBy(coverages, 'date').map((coverage) => {
            const documents =
              documentReferencesByCoverage[coverage.reference.reference] || []
            const isComplete = documents.length > 0

            return (
              <CoverageCard
                key={coverage.id}
                title={coverage.typeDisplay}
                description={`Upload proof of ${coverage.typeDisplay} ID coverage and enrollment.`}
                isComplete={isComplete}
                coverage={coverage}
                documents={documents}
                onUpload={(attachments) => handleUpload(coverage, attachments)}
                onLinkDocuments={() => {
                  setSelectedCoverage(coverage)
                  openLinkDocumentsModal()
                }}
                onUnlinkDocument={(docId) => {
                  const doc = documents.find((d) => d.id === docId)
                  if (doc) {
                    patchDocumentMutation.mutate(
                      {
                        instance: doc,
                        mutateFn: (document) => {
                          document.coverage = undefined
                        },
                      },
                      {
                        onSuccess: () => {
                          void refetchDocumentReferences()
                        },
                      }
                    )
                  }
                }}
                onEditCoverage={() => {
                  setSelectedCoverage(coverage)
                  openEditModal()
                }}
                onUpdateDocumentTitle={(docId, index, title) => {
                  const doc = documents.find((d) => d.id === docId)
                  if (doc) {
                    patchDocumentMutation.mutate(
                      {
                        instance: doc,
                        mutateFn: (document) => {
                          document.setAttachmentTitleAtIndex(index, title)
                        },
                      },
                      {
                        onSuccess: () => {
                          void refetchDocumentReferences()
                        },
                      }
                    )
                  }
                }}
              />
            )
          })}
        </div>
      </div>

      <CoverageDialog
        open={showCreateModal}
        onOpenChange={(open) => {
          if (!open) {
            closeCreateModal()
          }
        }}
      />

      {selectedCoverage && (
        <LinkDocumentsModal
          open={showLinkDocumentsModal}
          onOpenChange={(open) => {
            if (!open) {
              closeLinkDocumentsModal()
              setSelectedCoverage(null)
            }
          }}
          coverage={selectedCoverage}
          onSuccess={() => {
            void refetchDocumentReferences()
          }}
        />
      )}

      {selectedCoverage && (
        <CoverageDialog
          coverage={selectedCoverage}
          open={showEditModal}
          onOpenChange={(open) => {
            if (!open) {
              closeEditModal()
              setSelectedCoverage(null)
            }
          }}
        />
      )}
    </>
  )
}
